import { useApiServiceModels } from '../../services/apiServiceModels.js';
import FormCard from './FormCard';
import Select from 'react-select';
import { customDropdownStyles } from '../../data/data';
import { useEffect, useState } from 'react';
import { formValidationSchemaModels } from '../../schema/formValidationSchema.js';
import ModelsDataTable from '../Common/ModelsDataTable.jsx';
import Loading from '../Common/Loading.jsx';
import toast from 'react-hot-toast';

const ModelsForm = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const datasourceParam = queryParams.get('datasource');
  const tagsParam = queryParams.getAll('tags');
  const tagNames = queryParams.getAll('tagName');

  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [count, setCount] = useState(0);
  const [modelsData, setModelsData] = useState([]);

  const [dropdownOptions, setDropdownOptions] = useState({
    assemblyOptions: [],
    dryGasSealTagsOptions: [],
  });

  const [selectedValues, setSelectedValues] = useState({
    selectedAssembly: null,
    selectedDryGasSealTags: null,
  });

  const mapAssembly = (item) => ({
    label: item.DRIVER + " " + item.PROJECTNAME,
    value: item.DRIVER + " " + item.PROJECTNAME,
    datasource: item.DATASOURCE,
  });

  const mapDryGasSealTags = (item) => {
    const depTagAliasArray = item.DEP_TAG_ALIAS.replace(/[[\]']/g, '').split(', ');
    const formattedString = depTagAliasArray.map(tag => `${tag}`).join(', ');

    return {
      label: formattedString,
      value: formattedString,
      depTagAliasArray: item.DEP_TAG_ALIAS,
      datasource: item.DATASOURCE,
    };
  };



  const handleModelsData = async (datasource, tags, limit) => {

    setLoading(true);
    try {
      setModelsData([]);
      const count = await getCount(datasource, tags);

      setCount(count.COUNT);

      const modelsData = await getModelsData(datasource, tags, limit);
      setModelsData((prev) => [...prev, ...modelsData]);

      setLoading(false);
    } catch (e) {
      console.log(e);
      toast.error("Error fetching models data. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // Helper to reload models with current filters
  const reloadModels = () => {
    const datasource = selectedValues.selectedAssembly?.datasource ?? datasourceParam;
    const tags = selectedValues.selectedDryGasSealTags?.depTagAliasArray ?? tagsParam;
    handleModelsData(datasource, tags, 10);
  };

  const {
    getAssemblies,
    getDryGasSealTags,
    getCount,
    getModelsData,
    updateModelStatus,
    deleteModel,
  } = useApiServiceModels();

  const fetchDropdownData = async (dropdownName, apiCall, mapFunction, datasource = null) => {
    try {
      const data = await apiCall(datasource);
      if (Array.isArray(data)) {
        const options = data.map(mapFunction);
        setDropdownOptions((prev) => ({ ...prev, [`${dropdownName}Options`]: options }));

        // Set default selected values based on URL parameters
        if (dropdownName === 'assembly' && datasourceParam) {
          const defaultAssembly = options.find(option => option.datasource == datasourceParam);
          console.log(" Default Assembly from URL:", defaultAssembly);
          setSelectedValues((prev) => ({ ...prev, selectedAssembly: defaultAssembly }));
          await fetchDropdownData('dryGasSealTags', getDryGasSealTags, mapDryGasSealTags, datasourceParam);
        }

        if (dropdownName === 'dryGasSealTags' && tagsParam.length > 0) {
          // Normalize the tagsParam
          const tagsArray = tagsParam.map(tag => tag.trim().toLowerCase());

          const defaultTags = options.find(option => {
            // Split and normalize the option value
            const optionTagsArray = option.value.split(',').map(tag => tag.trim().toLowerCase());

            // Check if tagsArray and optionTagsArray are exactly the same
            return tagsArray.length === optionTagsArray.length && tagsArray.every((tag, index) => tag === optionTagsArray[index]);
          });

          setSelectedValues((prev) => ({ ...prev, selectedDryGasSealTags: defaultTags }));
          handleModelsData(datasourceParam, tagsParam, 10);

        }
      } else {
        setDropdownOptions((prev) => ({ ...prev, [`${dropdownName}Options`]: [] }));
      }
    } catch (error) {
      toast.error(`Error fetching ${dropdownName} data. Please try again.`);
      console.error(`Error fetching ${dropdownName} data in Models Screen:`, error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchDropdownData('assembly', getAssemblies, mapAssembly);
      setLoading(false);
    };

    fetchData();

  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (modelsData.some(model => model.TRAINING_STATUS === 'RUNNING')) {

        handleModelsData(datasourceParam ?? selectedValues.selectedDryGasSealTags.datasource, tagsParam.length === 0 ? selectedValues.selectedDryGasSealTags.depTagAliasArray : tagsParam, 10);
      }
    }, 15000); // Check every 15 seconds

    // Clear the interval on component unmount
    return () => clearInterval(interval);
  }, [modelsData, datasourceParam, tagsParam]);

  const validateField = (fieldName, value) => {
    formValidationSchemaModels
      .validateAt(fieldName, { [fieldName]: value })
      .then(() => {
        setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: null }));
      })
      .catch((validationError) => {
        setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: validationError.message }));
      });
  };

  const handleSelectChange = async (name, selectedOption) => {
    // If assembly is changed, clear DGS tags selection as well
    if (name === 'selectedAssembly') {
      setSelectedValues((prev) => ({
        ...prev,
        selectedAssembly: selectedOption,
        selectedDryGasSealTags: null,
      }));
    } else {
      setSelectedValues((prev) => ({ ...prev, [name]: selectedOption }));
    }

    const fieldsToValidate = ['selectedAssembly', 'selectedDryGasSealTags'];

    if (fieldsToValidate.includes(name)) {
      validateField(name, selectedOption);
    }

    const dataSourceForDgsTags = name === 'selectedAssembly' ? selectedOption?.datasource : selectedValues.selectedAssembly?.datasource;
    if (name === 'selectedAssembly' && selectedOption) {
      const datasource = selectedOption.datasource;

      setLoading(true);

      try {
        await fetchDropdownData('dryGasSealTags', getDryGasSealTags, mapDryGasSealTags, datasource);
      } catch (e) {
        console.log(e);
        toast.error("Error fetching Dry Gas Seal Tags. Please try again.");
        setLoading(false);
      } finally {
        setLoading(false);
      }
    } else if (name === 'selectedDryGasSealTags' && selectedOption) {
      const tagAliases = selectedOption.depTagAliasArray;

      setLoading(true);

      handleModelsData(dataSourceForDgsTags, tagAliases, 10)
    }
  };

  return (
    <>
      {loading && <Loading />}
      <div className='flex justify-center flex-col gap-10 items-center mt-4 mb-40'>
        <FormCard>
          <div>
            <span className='font-semibold text-2xl text-btn-primary'>Models</span>
            <p className='mt-3 text-[14.5px]'>View the list of pre-trained models along with their accuracy and training details to Activate or Deactivate as per the requirement</p>

            <form action="" className='mt-8'>
              <div className='flex gap-4'>
                <div className='flex flex-col w-full'>
                  <label htmlFor="assembly" className='mb-1'>Assembly*</label>
                  <Select
                    id="assembly"
                    className='w-full'
                    placeholder='Select Assembly'
                    options={dropdownOptions.assemblyOptions.filter(opt => opt.value !== null && opt.value !== undefined && opt.value !== '')}
                    isClearable
                    isSearchable
                    required
                    styles={customDropdownStyles}
                    value={selectedValues.selectedAssembly}
                    onChange={(option) => handleSelectChange('selectedAssembly', option)}
                  />
                  {errors.selectedAssembly && <p className="text-red-500 text-[14.5px] mt-1">{errors.selectedAssembly}</p>}
                </div>

                <div className='flex flex-col w-full'>
                  <label htmlFor="dgs-tags" className='mb-1'>Dry Gas Seal Tags*</label>
                  <Select
                    id="dgs-tags"
                    placeholder='Select Dry Gas Seal Tags'
                    isClearable
                    isSearchable
                    required
                    styles={customDropdownStyles}
                    options={dropdownOptions.dryGasSealTagsOptions.filter(opt => opt.value !== null && opt.value !== undefined && opt.value !== '')}
                    value={selectedValues.selectedDryGasSealTags}
                    onChange={(option) => handleSelectChange('selectedDryGasSealTags', option)}
                  />
                  {errors.selectedDryGasSealTags && <p className="text-red-500 text-[14.5px] mt-1">{errors.selectedDryGasSealTags}</p>}
                </div>
              </div>
            </form>
          </div>
        </FormCard>

        {modelsData.length !== 0 && selectedValues.selectedAssembly && selectedValues.selectedDryGasSealTags &&
          <ModelsDataTable
            tagName={tagNames}
            modelsData={modelsData}
            count={count}
            updateModelStatus={updateModelStatus}
            loading={loading}
            setLoading={setLoading}
            deleteModel={deleteModel}
            reloadModels={reloadModels}
          />
        }
      </div>
    </>
  );
};

export default ModelsForm;
